import {
  AbstractControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { SubscriptionLike } from 'rxjs';
import { delay } from 'rxjs/operators';

import { TypedFormArray, TypedFormControl, TypedFormGroup } from './typed-forms';

export const duplicateValidator =
  <TControl extends AbstractControl, TValue>(
    getControl: (c: TControl) => TypedFormControl<TValue | null> | TypedFormControl<TValue>,
  ) =>
  (control: TypedFormGroup<Record<string, TControl>> | TypedFormArray<TControl>) => {
    const existing = new Set<TValue>();
    const duplicates = new Set<TValue>();

    for (const key of Object.keys(control.controls)) {
      const value = getControl(control.controls[key]).value;

      if (value) {
        if (existing.has(value)) {
          duplicates.add(value);
        } else {
          existing.add(value);
        }
      }
    }

    if (duplicates.size > 0) {
      return {
        duplicates: Array.from(duplicates),
      };
    }

    return null;
  };

export const duplicateValidatorWithEnabler =
  <TControl extends AbstractControl, TValue>(
    getControlValue: (c: TControl) => TypedFormControl<TValue | null> | TypedFormControl<TValue>,
    getControlEnabled: (c: TControl) => TypedFormControl<boolean>,
  ) =>
  (control: TypedFormGroup<Record<string, TControl>> | TypedFormArray<TControl>) => {
    const existing = new Set<TValue>();
    const duplicates = new Set<TValue>();

    for (const key of Object.keys(control.controls)) {
      const value = getControlValue(control.controls[key]).value;
      const isEnabled = getControlEnabled(control.controls[key]).value;

      if (!isEnabled) {
        continue;
      }

      if (value) {
        if (existing.has(value)) {
          duplicates.add(value);
        } else {
          existing.add(value);
        }
      }
    }

    if (duplicates.size > 0) {
      return {
        duplicates: Array.from(duplicates),
      };
    }

    return null;
  };

export const validateWithPrevious =
  <TGroup extends UntypedFormGroup, TValue = TGroup extends { value: infer U } ? U : never>(
    formArray: TypedFormArray<TGroup>,
    validate: (control: AbstractControl, previousValues: TValue[]) => ValidationErrors | null,
  ): ValidatorFn =>
  control => {
    const index = formArray.controls.indexOf(control.parent as TGroup);
    if (index === -1) {
      return null;
    }
    const formValues = formArray.controls.slice(0, index).map(c => c.value);

    return validate(control, formValues);
  };

export const validateIfNotLast =
  <TGroup extends UntypedFormGroup>(
    formArray: TypedFormArray<TGroup>,
    validate: ValidatorFn,
  ): ValidatorFn =>
  control => {
    const index = formArray.controls.indexOf(control.parent as TGroup);
    if (index === -1) {
      return null;
    }
    if (index + 1 === formArray.controls.length) {
      return null;
    }
    return validate(control);
  };

export const requiredWith = (
  getOtherControl: (control: AbstractControl) => AbstractControl | undefined,
): ValidatorFn => {
  let currentOtherControl: AbstractControl | undefined;
  let subscription: SubscriptionLike | undefined;

  return control => {
    const otherControl = getOtherControl(control);
    if (!otherControl) {
      return null;
    }

    if (currentOtherControl !== otherControl) {
      currentOtherControl = otherControl;

      subscription?.unsubscribe();
      subscription =
        otherControl &&
        otherControl.valueChanges.pipe(delay(1)).subscribe(() => control.updateValueAndValidity());
    }

    if (otherControl?.enabled && otherControl?.value) {
      return Validators.required(control);
    }

    return null;
  };
};

export function isEmptyInputValue(value: any): boolean {
  return (
    value == null ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'string' && value.trim().length === 0)
  );
}
